import { useRef, useState, useEffect } from "react";
import { Attribution } from "ol/control.js";
import { Form } from "react-bootstrap";

import styles from "./Map.module.css";
import MapContext from "./MapContext";

import olMap from "ol/Map";
import olView from "ol/View";
import TileLayer from "ol/layer/Tile";
import styled from "styled-components";
import OSM from "ol/source/OSM";
import XYZ from "ol/source/XYZ";

import { Size as olSize } from "ol/size";

export const SatelliteSwitch = styled.div`
  position: fixed; /* always visible on screen */
  bottom: 30px;
  right: 0.5em;
  z-index: 101;

  background: var(--bs-body-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color);
  padding: 10px 14px;
  border-radius: 8px;

  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  display: flex;
  align-items: center;
  gap: 8px;

  font-size: 14px;
  `;


export interface MapProps {
  view?: olView;
  size?: olSize;
  children: React.ReactNode;
}
/**
 * Component to render the map wrapper and instantiate an OL Map within it.
 * Allows child components to be rendered inside, allowing further customisation
 * and to specify other map configuration such as layers or control in a markup fashion.
 * @component
 * @param {view} OpenLayers View for the map
 * @param {React.ReactNode} children - the children components to render
 * @example
 * <Map view={view}>
 *   <Layers>
 *     <Tile source={osmSource} />
 *   </Layers>
 *   <Controls>
 *     <FullScreen />
 *   </Controls>
 * </Map>
 * @remarks
 * Note, uses the MapContext to make the OL Map object available to child components.
 * Use useContext(MapContext) to obtain this OL Map object.
 */
const Map = ({ view, size, children }: MapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);

  const [map, setMap] = useState<olMap | null>(null);
  const [isSatellite, setIsSatellite] = useState(false);

  const streetLayerRef = useRef<TileLayer<any> | null>(null);
  const satelliteLayerRef = useRef<TileLayer<any> | null>(null);

  const applyBaseLayerVisibility = (
    mapInstance: olMap | null,
    satelliteEnabled: boolean
  ) => {
    if (!mapInstance) return;

    mapInstance.getLayers().forEach((layer) => {
      if (!(layer instanceof TileLayer)) return;
      const source = layer.getSource();
      if (source instanceof OSM) {
        layer.setVisible(!satelliteEnabled);
      }
    });

    satelliteLayerRef.current?.setVisible(satelliteEnabled);
  };

  useEffect(() => {
    const attribution = new Attribution({
      collapsible: false,
    });

    const streetLayer = new TileLayer({
      visible: true,
      source: new OSM(),
    });

    const satelliteLayer = new TileLayer({
      visible: false,
      source: new XYZ({
        url: "https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2024_3857/default/g/{z}/{y}/{x}.jpg",
        attributions:
          '© Sentinel-2 cloudless by EOX IT Services GmbH (Contains modified Copernicus Sentinel data 2024)',
        maxZoom: 19,
      }),
    });

    streetLayerRef.current = streetLayer;
    satelliteLayerRef.current = satelliteLayer;

    const mapObject = new olMap({
      view: view,
      layers: [streetLayer, satelliteLayer],
      controls: [attribution],
      overlays: [],
    });

    mapObject.setTarget(mapRef.current as HTMLDivElement);
    setMap(mapObject);

    return () => mapObject.setTarget(undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map || !view || !mapRef.current) return;
    map.setView(view);
  }, [view]);

  useEffect(() => {
    if (!map) return;
    map.setSize(size);
  }, [size]);

  useEffect(() => {
    applyBaseLayerVisibility(map, isSatellite);
  }, [map, isSatellite]);

  const handleSatelliteToggle = (checked: boolean) => {
    setIsSatellite(checked);
    applyBaseLayerVisibility(map, checked);
  };

  return (
    <MapContext.Provider value={map}>
      <SatelliteSwitch >
        <Form.Check
          id="satellite-view-toggle"
          type="switch"
          label="Satellite View"
          checked={isSatellite}
          onChange={(e) => handleSatelliteToggle(e.target.checked)}
        />
      </SatelliteSwitch>
      <div ref={mapRef} className={styles.map}></div>

      {children}
    </MapContext.Provider>
  );
};

export default Map;